import { useCallback, useEffect, useState } from 'react';
import { api, type InstallerRow, type Summary, type UnitRow, type WorkOrderRow } from '../lib/api';
import { Empty, ErrorBanner, Panel, Spinner, StatusBadge } from '../lib/ui';
import { JOB_TYPES, JOB_TYPE_LABELS } from '../../shared/domain';
import type { WorkOrderStatus } from '../../shared/work-order-status';

/**
 * The board, in the three states an operator actually asks about:
 * out with a crew, back and waiting on us, and finished.
 *
 * A single filterable list forced you to know which of ten statuses to pick
 * before it told you anything. The grouping below is the question people
 * actually arrive with — "what's outstanding?" — and the middle section is the
 * one that matters most, because a job sitting in review is one nobody has
 * invoiced.
 */

interface Section {
  key: string;
  title: string;
  blurb: string;
  statuses: WorkOrderStatus[];
  /** Highlighted when non-empty: these are waiting on us, not on a crew. */
  actionable?: boolean;
}

const SECTIONS: Section[] = [
  {
    key: 'dispatched',
    title: 'Dispatched',
    blurb: 'Out with a crew — sent, acknowledged or under way.',
    statuses: ['dispatched', 'acknowledged', 'in_progress'],
  },
  {
    key: 'review',
    title: 'Completed — waiting for review',
    blurb: 'The crew is done. Nothing reaches the client, and nothing gets invoiced, until someone approves these.',
    statuses: ['submitted'],
    actionable: true,
  },
  {
    key: 'finaled',
    title: 'Finaled',
    blurb: 'Approved and closed.',
    statuses: ['approved', 'closed'],
  },
];

export function Dashboard({ navigate }: { navigate: (path: string) => void }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<WorkOrderRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get<Summary>('/api/admin/work-orders/summary'),
      api.get<{ workOrders: WorkOrderRow[] }>('/api/admin/work-orders?limit=200'),
    ])
      .then(([s, w]) => {
        setSummary(s);
        setRows(w.workOrders);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, []);

  useEffect(load, [load]);

  const attention = summary?.needsAttention;

  // Anything not in one of the three sections — drafts awaiting review,
  // rejected, cancelled. Shown separately rather than hidden: a draft nobody
  // ever looks at is how an install request gets missed.
  const grouped = new Map<string, WorkOrderRow[]>();
  const other: WorkOrderRow[] = [];
  for (const row of rows ?? []) {
    const section = SECTIONS.find((s) => s.statuses.includes(row.wo.status));
    if (section === undefined) other.push(row);
    else grouped.set(section.key, [...(grouped.get(section.key) ?? []), row]);
  }

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}

      <div className="flex flex-wrap items-center gap-2">
        <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile
            label="Drafts to review"
            value={attention?.draftsPendingReview}
            urgent={(attention?.draftsPendingReview ?? 0) > 0}
          />
          <Tile
            label="Unacknowledged"
            value={attention?.unacknowledgedDispatches}
            urgent={(attention?.unacknowledgedDispatches ?? 0) > 0}
          />
          <Tile label="In progress" value={attention?.inProgress} />
          <Tile
            label="Awaiting approval"
            value={attention?.awaitingApproval}
            urgent={(attention?.awaitingApproval ?? 0) > 0}
          />
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="bg-harkey-gold px-3 py-2 text-xs font-semibold text-black"
        >
          {showCreate ? 'Cancel' : '+ New work order'}
        </button>
      </div>

      {showCreate && (
        <CreateWorkOrder
          onCreated={(id) => {
            setShowCreate(false);
            navigate(`/app/work-orders/${id}`);
          }}
          onError={setError}
        />
      )}

      {rows === null ? (
        <Spinner />
      ) : (
        <>
          {SECTIONS.map((section) => {
            const list = grouped.get(section.key) ?? [];
            return (
              <Panel
                key={section.key}
                title={`${section.title} (${list.length})`}
                actions={
                  section.actionable === true && list.length > 0 ? (
                    <span className="bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                      needs you
                    </span>
                  ) : undefined
                }
              >
                <p className="mb-2 text-xs text-neutral-500">{section.blurb}</p>
                {list.length === 0 ? (
                  <Empty>Nothing here.</Empty>
                ) : (
                  <WorkOrderTable rows={list} navigate={navigate} />
                )}
              </Panel>
            );
          })}

          {other.length > 0 && (
            <Panel title={`Everything else (${other.length})`}>
              <p className="mb-2 text-xs text-neutral-500">
                Drafts, rejected and cancelled jobs — not out with a crew and not finished.
              </p>
              <WorkOrderTable rows={other} navigate={navigate} />
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

function WorkOrderTable({
  rows,
  navigate,
}: {
  rows: WorkOrderRow[];
  navigate: (path: string) => void;
}) {
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="border-b border-neutral-200 text-left text-neutral-500 dark:border-neutral-800">
          <th className="py-1 pr-3 font-medium">WO</th>
          <th className="py-1 pr-3 font-medium">Type</th>
          <th className="py-1 pr-3 font-medium">Status</th>
          <th className="py-1 pr-3 font-medium">Unit</th>
          <th className="py-1 pr-3 font-medium">Client</th>
          <th className="py-1 pr-3 font-medium">Installer</th>
          <th className="py-1 font-medium">Install date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ wo, unitCode, unitCity, clientName, installerName }) => (
          <tr
            key={wo.id}
            tabIndex={0}
            onClick={() => navigate(`/app/work-orders/${wo.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') navigate(`/app/work-orders/${wo.id}`);
            }}
            className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 dark:border-neutral-900 dark:hover:bg-neutral-900"
          >
            <td className="py-1.5 pr-3 font-medium tabular-nums">{wo.woNumber}</td>
            <td className="py-1.5 pr-3">
              {wo.jobType === 'repair' ? (
                <span className="bg-blue-100 px-1.5 py-0.5 text-blue-900 dark:bg-blue-900 dark:text-blue-100">
                  repair
                </span>
              ) : (
                <span className="text-neutral-500">install</span>
              )}
            </td>
            <td className="py-1.5 pr-3">
              <StatusBadge status={wo.status} />
              {wo.needsManualUnit && (
                <span className="ml-1 text-red-600 dark:text-red-400" title="Unit not confirmed">
                  ⚑
                </span>
              )}
            </td>
            <td className="py-1.5 pr-3">
              {unitCode ?? <span className="text-red-600 dark:text-red-400">not set</span>}
              {unitCity !== null && <span className="text-neutral-500"> · {unitCity}</span>}
            </td>
            <td className="py-1.5 pr-3">{clientName ?? '—'}</td>
            <td className="py-1.5 pr-3">{installerName ?? '—'}</td>
            <td className="py-1.5 tabular-nums">
              {wo.requestedInstallDateText ?? wo.requestedInstallDate ?? '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Raising a job by hand.
 *
 * Most work orders arrive by email, but not all: a repair spotted by a crew or
 * phoned in by a landlord has no email to parse, and until now those had no way
 * in at all.
 *
 * Creates a draft and opens it. Everything else — dispatching, the PDF, the
 * installer link — happens on the detail screen through the same status machine
 * an emailed job goes through, so a hand-raised job cannot skip a step.
 */
function CreateWorkOrder({
  onCreated,
  onError,
}: {
  onCreated: (id: string) => void;
  onError: (e: unknown) => void;
}) {
  const [jobType, setJobType] = useState<'install' | 'repair'>('install');
  const [unitQuery, setUnitQuery] = useState('');
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [unitId, setUnitId] = useState('');
  const [installers, setInstallers] = useState<InstallerRow[]>([]);
  const [installerId, setInstallerId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [copyDescription, setCopyDescription] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [requestedInstallDate, setRequestedInstallDate] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ installers: InstallerRow[] }>('/api/admin/installers')
      .then((d) => setInstallers(d.installers.filter((i) => i.isActive)))
      .catch(() => undefined);
  }, []);

  // Digital faces are excluded: they take no vinyl, so an install against one
  // is almost always a mistyped code. A repair on a digital is still reachable
  // by searching for it directly.
  useEffect(() => {
    const timer = setTimeout(() => {
      const installable = jobType === 'install' ? '&installableOnly=true' : '';
      api
        .get<{ units: UnitRow[] }>(
          `/api/admin/units?q=${encodeURIComponent(unitQuery)}&limit=25${installable}`,
        )
        .then((d) => setUnits(d.units))
        .catch(() => undefined);
    }, 200);
    return () => clearTimeout(timer);
  }, [unitQuery, jobType]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post<{ workOrder: { id: string } }>('/api/admin/work-orders', {
        jobType,
        unitId: unitId === '' ? null : unitId,
        installerId: installerId === '' ? null : installerId,
        campaignName: campaignName.trim() === '' ? null : campaignName.trim(),
        copyDescription: copyDescription.trim() === '' ? null : copyDescription.trim(),
        specialInstructions:
          specialInstructions.trim() === '' ? null : specialInstructions.trim(),
        requestedInstallDate: requestedInstallDate === '' ? null : requestedInstallDate,
      });
      onCreated(res.workOrder.id);
    } catch (err: unknown) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  const selectedUnit = units.find((u) => u.id === unitId);

  return (
    <Panel title="New work order">
      <form onSubmit={submit} className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">Job type</span>
            <select
              value={jobType}
              onChange={(e) => {
                setJobType(e.target.value as 'install' | 'repair');
                setUnitId('');
              }}
              className="border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            >
              {JOB_TYPES.map((t) => (
                <option key={t} value={t}>
                  {JOB_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
              Find the unit {jobType === 'install' && '(digital faces hidden)'}
            </span>
            <input
              value={unitQuery}
              onChange={(e) => setUnitQuery(e.target.value)}
              placeholder="Code, address, city…"
              className="w-56 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">Unit</span>
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className="w-72 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            >
              <option value="">— not set —</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.unitCode} · {u.city ?? u.dma ?? 'unknown'}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
              Installer{' '}
              {selectedUnit?.defaultInstallerName !== null &&
                selectedUnit?.defaultInstallerName !== undefined && (
                  <span className="text-neutral-500">
                    (usual: {selectedUnit.defaultInstallerName})
                  </span>
                )}
            </span>
            <select
              value={installerId}
              onChange={(e) => setInstallerId(e.target.value)}
              className="w-56 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            >
              <option value="">— decide later —</option>
              {installers.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.companyName}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
              {jobType === 'repair' ? 'Requested date' : 'Install date'}
            </span>
            <input
              type="date"
              value={requestedInstallDate}
              onChange={(e) => setRequestedInstallDate(e.target.value)}
              className="border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
              {jobType === 'repair' ? 'Reference' : 'Campaign'}
            </span>
            <input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder={jobType === 'repair' ? 'Storm damage — south face' : 'Advertiser — flight'}
              className="w-64 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
        </div>

        {/* Copy only makes sense when something is being hung. A repair has no
            artwork, so the field is not offered rather than left blank. */}
        {jobType === 'install' && (
          <label className="block text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">Copy</span>
            <input
              value={copyDescription}
              onChange={(e) => setCopyDescription(e.target.value)}
              className="w-full border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
        )}

        <label className="block text-xs">
          <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
            {jobType === 'repair' ? 'What needs repairing' : 'Special instructions'}
          </span>
          <textarea
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            rows={2}
            placeholder={
              jobType === 'repair'
                ? 'Torn vinyl bottom left; two lamps out on the north face.'
                : ''
            }
            className="w-full border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
          />
        </label>

        <button
          type="submit"
          disabled={busy}
          className="bg-harkey-gold px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create draft'}
        </button>
        <p className="text-xs text-neutral-500">
          Creates a draft and opens it. Dispatching, the PDF and the installer link all happen
          there, through the same steps an emailed job goes through.
        </p>
      </form>
    </Panel>
  );
}

function Tile({
  label,
  value,
  urgent = false,
}: {
  label: string;
  value: number | undefined;
  urgent?: boolean;
}) {
  return (
    <div
      className={`border px-3 py-2 text-left ${
        urgent
          ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
          : 'border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900'
      }`}
    >
      <div className="text-2xl font-semibold tabular-nums">{value ?? '—'}</div>
      <div className="text-xs text-neutral-500">{label}</div>
    </div>
  );
}
